/**
 * A dashboard's screens.
 *
 * "Dashboard" always meant the named, permissioned, shareable thing — we simply
 * used the word for its one screen as well. A dashboard *has screens*: a live
 * timing table, a stint chart, and any number of widget grids. So the
 * collection, the rules, the URL and the share links are unchanged, and nothing
 * has to be migrated or renamed.
 *
 * The shape is deliberately the one widgets already use — a discriminated union
 * on `kind`, a mapped-type registry, per-kind parse with defaults on read, and
 * an unknown kind that becomes a dead tab rather than breaking the dashboard.
 * The difference between a grid and a timing table is not a difference in the
 * model: `grid` simply has a layout in its config and the others do not.
 */

import { defaultColumns, defaultRows, parseLayout } from './dashboard.ts'
import { toStoredLayout } from './layoutEdit.ts'
import type { CarRef, DashboardLayout, DataNeed } from './dashboard.ts'
import { neededData as widgetNeeds } from './dashboard.ts'

// --------------------------------------------------------------- columns

/** Columns a live timing table can show. */
export type TimingColumn =
  | 'position'
  | 'number'
  | 'state'
  | 'class'
  | 'classPosition'
  | 'driver'
  | 'team'
  | 'laps'
  | 'gap'
  | 'interval'
  | 's1'
  | 's2'
  | 's3'
  | 'last'
  | 'best'
  | 'pits'

export const timingColumns: readonly TimingColumn[] = [
  'position',
  'number',
  'state',
  'class',
  'classPosition',
  'driver',
  'team',
  'laps',
  'gap',
  'interval',
  's1',
  's2',
  's3',
  'last',
  'best',
  'pits',
]

export const timingColumnLabels: Record<TimingColumn, string> = {
  position: 'P',
  number: 'Num',
  state: 'State',
  class: 'Class',
  classPosition: 'PIC',
  driver: 'Driver',
  team: 'Team',
  laps: 'Laps',
  gap: 'Gap',
  interval: 'Int',
  s1: 'S1',
  s2: 'S2',
  s3: 'S3',
  last: 'Last',
  best: 'Best',
  pits: 'Pit',
}

// --------------------------------------------------------------- screens

export type ScreenConfig =
  | { kind: 'grid'; layout: DashboardLayout }
  | { kind: 'timing'; columns: TimingColumn[] }
  | { kind: 'stints' }
  | { kind: 'laps' }
  | { kind: 'planner' }
  | { kind: 'fuel' }

export type ScreenKind = ScreenConfig['kind']
export type ScreenConfigFor<K extends ScreenKind> = Extract<ScreenConfig, { kind: K }>

/**
 * One screen of a dashboard.
 *
 * `car` is what makes "one grid per car" work. A screen either inherits the
 * primary car from the URL or pins its own, and a widget then inherits the
 * screen's or pins its own — the same escape hatch at both levels, so two grids
 * side by side can follow two cars under one share link while a single unpinned
 * grid stays the parameterised thing that one link can point at any car.
 *
 * It applies to every kind, not only grids: a timing table and a stint chart
 * show the whole field, and which row is *yours* is what you look for first.
 */
export interface Screen {
  id: string
  name: string
  car?: CarRef
  config: ScreenConfig
}

/**
 * A screen this build does not know about.
 *
 * Kept rather than dropped, for the reason an unknown widget is: a dashboard
 * opened by an older client and saved must not silently lose the screens that
 * client could not draw.
 */
export interface UnknownScreen {
  id: string
  name: string
  unknownKind: string
}

export type ScreenEntry = Screen | UnknownScreen

export function isKnownScreen(e: ScreenEntry): e is Screen {
  return 'config' in e
}

// -------------------------------------------------------------- metadata

export interface ScreenMeta<K extends ScreenKind> {
  label: string
  description: string
  /** The name a newly added screen of this kind gets. */
  defaultName: string
  default: ScreenConfigFor<K>
  parse: (raw: Record<string, unknown>) => ScreenConfigFor<K>
  /**
   * How this kind's config is written.
   *
   * Per-kind for the same reason `parse` is. Persisting was generic while
   * reading was not, and that asymmetry is exactly how a grid's widgets went to
   * Firestore carrying `car: undefined` — which Firestore rejects outright, so
   * saving any dashboard with a grid failed. A kind that nests something knows
   * how to write it; nothing else has to.
   */
  toStored: (config: ScreenConfigFor<K>) => Record<string, unknown>
  /**
   * Whether a dashboard may hold more than one.
   *
   * Only the grid. It is the one screen whose content you author, so two of
   * them are genuinely two different things; every other kind is a fixed view
   * of whichever car the dashboard is on, and a second copy is a second tab
   * showing the same thing.
   *
   * Laps and the race plan were repeatable for a while, on the reasoning that a
   * team running two cars wants one of each side by side. That is a real want,
   * but the answer to it is the car selector, not a duplicate tab — and having
   * two kinds behave one way and two the other was a rule nobody could predict
   * from the outside.
   *
   * Only *adding* is blocked. A dashboard that already holds two lap screens
   * keeps them, because deleting somebody's screen to enforce a tidiness rule
   * is not a trade worth making.
   */
  repeatable: boolean
}

const screenDefaults: { [K in ScreenKind]: ScreenConfigFor<K> } = {
  grid: { kind: 'grid', layout: { columns: defaultColumns, rows: defaultRows, widgets: [] } },
  timing: {
    kind: 'timing',
    // Everything except team, which is the widest column and the least often
    // wanted — a car number identifies an entry to the people reading this.
    columns: [
      'position',
      'number',
      'state',
      'class',
      'classPosition',
      'driver',
      'laps',
      'gap',
      'interval',
      's1',
      's2',
      's3',
      'last',
      'best',
      'pits',
    ],
  },
  stints: { kind: 'stints' },
  laps: { kind: 'laps' },
  planner: { kind: 'planner' },
  fuel: { kind: 'fuel' },
}

export const screenMeta: { [K in ScreenKind]: ScreenMeta<K> } = {
  grid: {
    label: 'Widgets',
    description: 'A grid you arrange yourself, for one car.',
    defaultName: 'Dashboard',
    default: screenDefaults.grid,
    parse: (r) => ({ kind: 'grid', layout: parseLayout(r.layout) }),
    // Through toStoredLayout, which is what drops the `car: undefined` every
    // widget carries out of parsing.
    toStored: (c) => ({ kind: 'grid', layout: toStoredLayout(c.layout) }),
    repeatable: true,
  },
  timing: {
    label: 'Live timing',
    description: 'The whole field as a table, with race control above it.',
    defaultName: 'Timing',
    default: screenDefaults.timing,
    parse: (r) => ({
      kind: 'timing',
      columns: subsetOf(r.columns, timingColumns, screenDefaults.timing.columns),
    }),
    toStored: (c) => ({ kind: 'timing', columns: [...c.columns] }),
    repeatable: false,
  },
  stints: {
    label: 'Stints',
    description: 'Every car’s stints across the session, on one time axis.',
    defaultName: 'Stints',
    default: screenDefaults.stints,
    parse: () => ({ ...screenDefaults.stints }),
    toStored: () => ({ kind: 'stints' }),
    repeatable: false,
  },
  laps: {
    label: 'Lap times',
    description: 'Every lap for one car, with the numbers that come out of them.',
    defaultName: 'Laps',
    default: screenDefaults.laps,
    parse: () => ({ ...screenDefaults.laps }),
    toStored: () => ({ kind: 'laps' }),
    repeatable: false,
  },
  planner: {
    label: 'Race plan',
    description: 'Pit windows and the driver plan, against what the car actually did.',
    defaultName: 'Plan',
    default: screenDefaults.planner,
    parse: () => ({ ...screenDefaults.planner }),
    toStored: () => ({ kind: 'planner' }),
    repeatable: false,
  },
  fuel: {
    label: 'Fuel',
    description: 'Every fill this session, matched to its pit stop, with how far the fuel went.',
    defaultName: 'Fuel',
    default: screenDefaults.fuel,
    parse: () => ({ ...screenDefaults.fuel }),
    toStored: () => ({ kind: 'fuel' }),
    repeatable: false,
  },
}

export const screenKinds = Object.keys(screenMeta) as ScreenKind[]

function isScreenKind(v: unknown): v is ScreenKind {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(screenMeta, v)
}

/** Everything a screen reads. A grid's needs are its widgets'. */
export function screenNeeds(screen: ScreenEntry): DataNeed[] {
  if (!isKnownScreen(screen)) return []
  switch (screen.config.kind) {
    case 'grid':
      return widgetNeeds(screen.config.layout.widgets)
    case 'timing':
      return ['session', 'standings', 'liveMessages']
    case 'stints':
      return ['session', 'standings', 'stints', 'fcy']
    case 'laps':
      // allLaps rather than laps: every widget that shows recent laps is happy
      // with a window, and this page is the one thing that is not — an average
      // over the last forty laps of a twenty-five hour race is not the average
      // anybody asked for.
      return ['session', 'allLaps', 'stints']
    case 'planner':
      // pitStops for the visits a driver stint is cut at, and stints for the
      // driver the timing system named in each pit stint, which is who is
      // driving wherever the team has not declared. fcy for the track band,
      // which is what makes a window missed under a safety car legible as
      // something other than a mistake.
      return ['session', 'standings', 'pitStops', 'stints', 'fcy']
    case 'fuel':
      return ['session', 'fuel']
  }
}

/** Every distinct listener a set of screens requires, and no more. */
export function neededDataForScreens(screens: readonly ScreenEntry[]): DataNeed[] {
  const needs = new Set<DataNeed>()
  for (const s of screens) for (const n of screenNeeds(s)) needs.add(n)
  return [...needs]
}

// ----------------------------------------------------------------- parse

/**
 * Read a dashboard's screens.
 *
 * `legacyLayout` is the migration, and it is the whole of it: a document
 * written before screens existed carries a bare `layout`, which becomes one
 * grid screen on read. No batch migration, and every existing dashboard opens
 * unchanged.
 */
export function parseScreens(raw: unknown, legacyLayout?: unknown): ScreenEntry[] {
  const list = Array.isArray(raw) ? raw : []
  const out = list.map((s, i) => parseScreen(s, i)).filter((s): s is ScreenEntry => s !== null)
  if (out.length > 0) return out

  const layout = parseLayout(legacyLayout)
  if (layout.widgets.length === 0) return []
  return [
    {
      id: 'grid-1',
      name: screenMeta.grid.defaultName,
      config: { kind: 'grid', layout },
    },
  ]
}

function parseScreen(raw: unknown, index: number): ScreenEntry | null {
  const r = asRecord(raw)
  const id = typeof r.id === 'string' && r.id ? r.id : `screen-${index + 1}`
  const config = asRecord(r.config)

  if (!isScreenKind(config.kind)) {
    const kind = typeof config.kind === 'string' ? config.kind : 'unknown'
    return { id, name: nameOf(r.name, kind), unknownKind: kind }
  }

  const meta = screenMeta[config.kind] as ScreenMeta<ScreenKind>
  return {
    id,
    name: nameOf(r.name, meta.defaultName),
    car: parseCarRef(r.car),
    config: meta.parse(config),
  }
}

function nameOf(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() ? v.trim() : fallback
}

function parseCarRef(raw: unknown): CarRef | undefined {
  const r = asRecord(raw)
  if (r.kind === 'pinned' && typeof r.number === 'string' && r.number.trim()) {
    return { kind: 'pinned', number: r.number.trim() }
  }
  return undefined
}

// ------------------------------------------------------------- the source

// ----------------------------------------------------------- persistence

/**
 * Screens as they should be written.
 *
 * Drops `undefined`, which Firestore rejects outright, and writes only the
 * fields we own. An unknown kind is written back as it was read, so saving in an
 * older client does not delete the screens it could not draw.
 */
export function toStoredScreens(screens: readonly ScreenEntry[]): Record<string, unknown>[] {
  return screens.map((s) => {
    const base: Record<string, unknown> = { id: s.id, name: s.name }
    if (!isKnownScreen(s)) {
      base.config = { kind: s.unknownKind }
      return base
    }
    const meta = screenMeta[s.config.kind] as ScreenMeta<ScreenKind>
    base.config = meta.toStored(s.config)
    if (s.car) base.car = s.car
    return base
  })
}

export function screensEqual(a: readonly ScreenEntry[], b: readonly ScreenEntry[]): boolean {
  return JSON.stringify(toStoredScreens(a)) === JSON.stringify(toStoredScreens(b))
}

/** A readable, stable id for a new screen. */
export function nextScreenId(screens: readonly ScreenEntry[], kind: ScreenKind): string {
  const taken = new Set(screens.map((s) => s.id))
  for (let n = 1; ; n++) {
    const id = `${kind}-${n}`
    if (!taken.has(id)) return id
  }
}

/** Whether another screen of this kind may be added. */
export function canAddScreen(screens: readonly ScreenEntry[], kind: ScreenKind): boolean {
  if (screenMeta[kind].repeatable) return true
  return !screens.some((s) => isKnownScreen(s) && s.config.kind === kind)
}

export function addScreen(screens: readonly ScreenEntry[], kind: ScreenKind): ScreenEntry[] | null {
  if (!canAddScreen(screens, kind)) return null
  const meta = screenMeta[kind] as ScreenMeta<ScreenKind>
  const existing = screens.filter((s) => isKnownScreen(s) && s.config.kind === kind).length
  return [
    ...screens,
    {
      id: nextScreenId(screens, kind),
      // A second grid gets a distinguishing name, because "Dashboard" twice is
      // no use as a tab label.
      name: existing > 0 ? `${meta.defaultName} ${existing + 1}` : meta.defaultName,
      // Structured-cloned, so a new screen does not share the registry's default
      // object with every other screen of its kind.
      config: JSON.parse(JSON.stringify(meta.default)) as ScreenConfig,
    },
  ]
}

// --------------------------------------------------------------- helpers

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function subsetOf<T extends string>(v: unknown, allowed: readonly T[], fallback: readonly T[]): T[] {
  if (!Array.isArray(v)) return [...fallback]
  const seen = new Set<T>()
  const kept = v.filter((x): x is T => {
    if (typeof x !== 'string' || !(allowed as readonly string[]).includes(x)) return false
    if (seen.has(x as T)) return false
    seen.add(x as T)
    return true
  })
  return kept.length > 0 ? kept : [...fallback]
}
